import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useLazyQuery } from "@grandvisionhq/graphql";
import { useIntl, useApiConfig, useFeatureToggles } from "@grandvisionhq/state";
import { dispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { isNil } from "@grandvisionhq/utils";
import { toCartView } from "../../mappers/view/mappers/view/to-cart-view.js";
import { getFailureCodes, mapCartError } from "../../utils/utils/get-failure-error-codes.js";
import { useCartSettings } from "../../context/CartSettings/context/CartSettings/context.js";
import { CheckoutState } from "../../models/checkoutState/models/checkoutState/checkoutState.js";
import { getUnrecoverableValidationErrors } from "../../utils/utils/get-unrecoverable-validation-errors.js";
import { GET_CART } from "../../operations/queries/operations/queries/get-cart.js";
const useGetCart = ({
  cartCount,
  cartHash,
  cartId,
  onUnrecoverableErrors,
  onUpdateCartLocaleState,
  onFinalisedCartError,
  onCartDataLoadedSuccessfully
}) => {
  const settings = useCartSettings();
  const intl = useIntl();
  const { apiUrl } = useApiConfig();
  const { isClearCartOnAllErrorsEnabled } = useFeatureToggles();
  const handleTrackErrorTaelium = (errorDetails, errorMessage) => dispatchTrackErrorEvent(
    () => Promise.resolve({
      Error_Source: "Server",
      Error_Code: "Get cart error",
      Error_Details: errorDetails,
      ...errorMessage ? { Error_Message: errorMessage } : {}
    })
  );
  const handleGetCartCompleted = React.useCallback(
    (data2) => {
      const failures = data2?.cart?.failures;
      const validationErrors = data2?.cart?.validationErrors;
      const isFinalizedState = data2?.cart?.cart && data2.cart.cart.checkoutState == CheckoutState.Finalized;
      if (getUnrecoverableValidationErrors(validationErrors).length || failures?.length) {
        toast.failed("cart.errors.failedToLoadCart");
        if (failures) {
          getFailureCodes(failures).forEach((failureCode) => {
            console.error("cart error", failureCode);
          });
        }
        onUnrecoverableErrors?.();
      } else if (isFinalizedState) {
        toast.failed("cart.errors.failedToLoadCart");
        onFinalisedCartError?.();
      } else {
        onCartDataLoadedSuccessfully();
      }
    },
    [onUnrecoverableErrors, onFinalisedCartError, onCartDataLoadedSuccessfully]
  );
  const [getCart, { called, loading, data, error, refetch }] = useLazyQuery(GET_CART, {
    onCompleted: handleGetCartCompleted,
    onError: (error2) => {
      console.error(error2);
      handleTrackErrorTaelium(error2.name, error2.message);
      if (isClearCartOnAllErrorsEnabled) {
        onUnrecoverableErrors?.();
      }
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    // This causes a refetch to also trigger onCompleted again
    context: { forwardHeaders: { "cart-hash": cartHash } }
  });
  const refetchCallback = React.useCallback(() => {
    if (cartId) {
      refetch ? refetch({ id: cartId }) : getCart({ variables: { id: cartId } });
    }
  }, [getCart, refetch, cartId]);
  React.useEffect(() => {
    if (cartId) {
      getCart({ variables: { id: cartId } });
    }
  }, [cartId]);
  const fetchByCartId = React.useCallback(
    (cartIdParam, cartHashParam) => {
      onUpdateCartLocaleState?.({
        cartId: cartIdParam,
        cartHash: cartHashParam || cartHash,
        cartCount
      });
      settings?.setContext && settings.setContext({
        cartId: cartIdParam,
        ...cartHashParam && { cartHash: cartHashParam }
      });
      if (cartIdParam) {
        refetch ? refetch({ id: cartIdParam }) : getCart({
          variables: { id: cartIdParam },
          ...cartHashParam && { context: { forwardHeaders: { "cart-hash": cartHashParam } } }
        });
      }
    },
    [cartCount, cartHash, getCart, onUpdateCartLocaleState, refetch, settings]
  );
  const mappedCart = React.useMemo(() => {
    if (!cartId || data == null) return null;
    if (!data || !data?.cart) return null;
    if (isNil(data?.cart.cart)) {
      return null;
    }
    const isFinalizedState = data?.cart?.cart && data.cart.cart.checkoutState == CheckoutState.Finalized;
    if (isFinalizedState) return null;
    return toCartView(data?.cart.cart, data?.cart?.validationErrors, settings, intl, apiUrl);
  }, [data, intl, settings, apiUrl, cartId]);
  return React.useMemo(
    () => ({
      called,
      loading,
      data: mappedCart,
      error: !!data?.cart?.failures ? mapCartError(data?.cart.failures) : error,
      refetch: refetchCallback,
      fetchByCartId
    }),
    [called, mappedCart, data, error, fetchByCartId, loading, refetchCallback]
  );
};
export {
  useGetCart
};
//# sourceMappingURL=use-get-cart.js.map
